import React, { useState, useCallback } from "react";
import { BsPlusCircle, BsUpload, BsHeartPulse, BsListUl, BsExclamationTriangle } from "react-icons/bs";
import { filterconfigs } from "../../../configs/variantsConfig.js";
import { Button } from "react-bootstrap";
import SymbolsModal from "../../modals/SymbolsModal";
import { useTooltips, CollapsibleSection } from "../../shared";
import GenomicRangeInput from "./GenomicRangeInput";
import AttributeFilterInput from "./AttributeFilterInput";
import { ReactSortable } from "react-sortablejs";
import { getSidebarSectionVariant } from "../../../utils/filterColors";
import "../../../styles/utils/Sortable.css";
import "../../../styles/components/SidebarFilterSections.css";
import LoadingSpinner from "../../common/LoadingSpinner";

const FiltersForm = ({
  filterData,
  setFilterData,
  filters,
  setFilters,
  addFilter,
  handleShow,
  handlePanelShow,
  filter_results,
  showBuilder,
  gridRef,
  attributeFilterString = "",
  setAttributeFilterString,
}) => {
  const toggleCollapse = (panelID) => {
    const updatedFilters = {
      ...filters,
      [panelID]: {
        ...filters[panelID],
        collapsed: !filters[panelID].collapsed,
      },
    };
    setFilters(updatedFilters);
  };
  const [showModal, setShowModal] = useState(false);
  const closeModal = () => setShowModal(false);

  // Sortable sections configuration
  const [sortableSections, setSortableSections] = useState(() => {
    const defaultSections = [
      { id: "range", title: "Genomic Range", type: "range" },
      { id: "symbol", title: "Gene Symbols", type: "symbol" },
      { id: "attribute", title: "Attribute Filter", type: "attribute" }
    ];

    // Load saved order from localStorage, fallback to default
    const savedOrder = localStorage.getItem('sidebarFilterSectionOrder');
    if (savedOrder) {
      try {
        const parsedOrder = JSON.parse(savedOrder);
        // Merge with default sections to ensure new sections are included
        const existingIds = new Set(parsedOrder.map(section => section.id));
        const newSections = defaultSections.filter(section => !existingIds.has(section.id));
        return [...parsedOrder, ...newSections];
      } catch {
        // If parsing fails, use default
      }
    }
    return defaultSections;
  });

  // Loading state for adding filters
  const [addingFilters, setAddingFilters] = useState({});

  // Define your button handlers based on field ID
  const handleButtonClick = useCallback(async (fieldId, value, headerName) => {
    switch (fieldId) {
      case "specificFieldId1": // Replace with actual field ID
        // Logic for the first specific field
        // You can call a specific function or perform any actions
        break;

      case "feature": // Replace with actual field ID
        setShowModal(true);
        break;

      // Add more cases as needed for different field IDs
      default:
        // Default behavior - addFilter handles both new and editing cases
        setAddingFilters(prev => ({ ...prev, [fieldId]: true }));
        try {
          await addFilter(fieldId, value, headerName);
        } finally {
          setAddingFilters(prev => ({ ...prev, [fieldId]: false }));
        }
        break;
    }
  }, [addFilter]);

  const [filterDetails] = useState(
    filterconfigs.panels.reduce((acc, panel) => {
      panel.fields.forEach((field) => {
        acc[field.id] = field;
      });
      return acc;
    }, {})
  );

  // Function to normalize genomic range input
  const normalizeGenomicRange = (input) => {
    if (!input) return input;

    return (
      input
        // Remove extra whitespace
        .trim()
        // Normalize spaces around delimiters
        .replace(/\s*:\s*/g, ":")
        .replace(/\s*-\s*/g, "-")
        .replace(/\s*,\s*/g, ",")
        // Convert to lowercase for chr prefix consistency
        .replace(/CHR/gi, "chr")
    );
  };

  const validateText = (input, pattern, fieldDetail) => {
    if (!input) return { isValid: true, message: "" };

    // Clean up the input by trimming whitespace
    const cleanedInput = input.trim();
    if (!cleanedInput) return { isValid: true, message: "" };

    if (pattern.test(cleanedInput))
      return { isValid: true, message: "Valid input." };
    
    // Use field-specific validation message if available, otherwise provide generic message
    const validationMessage = fieldDetail.validationMessage || 
      `Invalid input format. Please check the expected format for ${fieldDetail.title || 'this field'}.`;
    
    return {
      isValid: false,
      message: validationMessage,
    };
  };

  const handleInputChange = useCallback((event) => {
    const { id, value } = event.target;

    // For region field, normalize the input for better user experience
    const processedValue =
      id === "region" ? normalizeGenomicRange(value) : value;

    const updatedData = {
      ...filterData,
      [id]: processedValue,
    };
    if (processedValue === "") {
      delete updatedData[id];
    }
    setFilterData(updatedData);
  }, [filterData, setFilterData]);

  const importTextFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".txt";
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        // append the text to the textarea
        const updatedData = {
          ...filterData,
          feature: filterData.feature ? filterData.feature + text : text,
        };
        setFilterData(updatedData);
      };
      reader.readAsText(file);
    };
    fileInput.click();
  };

  const renderTextField = useCallback((fieldObject, value, fieldDetail, validation) => {
    // Use GenomicRangeInput for region field, regular textarea for others
    if (fieldObject.field === "region") {
      return (
        <div className="mb-3">
          <GenomicRangeInput
            id={fieldObject.field}
            value={value}
            onChange={handleInputChange}
            validation={validation}
            placeholder={fieldDetail.placeholder || ""}
            rows={fieldDetail.row || 3}
          />
        </div>
      );
    }

    return (
      <div className="mb-3">
        <textarea
          id={fieldObject.field}
          name={fieldObject.field}
          value={value || ""}
          onChange={handleInputChange}
          className={`form-control ${!validation.isValid ? "is-invalid" : ""}`}
          placeholder={fieldDetail.placeholder || ""}
          rows={fieldDetail.row || 3}
        />
        {!validation.isValid && (
          <div className="invalid-feedback">
            <BsExclamationTriangle className="me-1" />
            {validation.message}
          </div>
        )}
      </div>
    );
  }, [handleInputChange]);

  const renderFields = useCallback(
    (panel) => {
      return panel.fields.map((fieldObject, index) => {
        const fieldDetail = filterDetails[fieldObject.field] || {};
        const value = filterData[fieldObject.field] || "";
        const validation = fieldDetail.Pattern
          ? validateText(value, fieldDetail.Pattern, fieldDetail)
          : { isValid: true, message: "" };
        return (
          <div key={index}>
            {fieldObject.cellDataType === "text" && (
              <>
                {renderTextField(fieldObject, value, fieldDetail, validation)}
                <div className="d-grid mb-3">
                  <button
                    className="btn btn-primary btn-sm btn-animate d-flex align-items-center justify-content-center gap-2"
                    onClick={() =>
                      handleButtonClick(
                        fieldObject.field,
                        value,
                        fieldObject.headerName
                      )
                    }
                    disabled={!validation.isValid || !value || addingFilters[fieldObject.field]}
                  >
                    {addingFilters[fieldObject.field] ? (
                      <LoadingSpinner size="1em" />
                    ) : (
                      <>
                        <BsPlusCircle />
                        Apply Filter
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      });
    },
    [filterDetails, filterData, handleButtonClick, renderTextField, addingFilters]
  );


  // Function to render section content based on type
  const renderSectionContent = (sectionType) => {
    switch (sectionType) {
      case "range":
        return renderFields(filters.range);
      case "symbol":
        return (
          <>
            <div className="gene-symbols-buttons-grid mb-3">
              <Button
                onClick={importTextFile}
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center justify-content-center btn-animate"
              >
                <BsUpload className="me-2" />
                Import Text File
              </Button>
              <Button
                onClick={handleShow}
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center justify-content-center btn-animate"
              >
                <BsHeartPulse className="me-2" />
                Select by Diseases
              </Button>
              <Button
                onClick={handlePanelShow}
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center justify-content-center btn-animate"
              >
                <BsListUl className="me-2" />
                Select by Panels
              </Button>
            </div>
            {renderFields(filters.symbol)}
            <SymbolsModal
              showModal={showModal}
              handleClose={closeModal}
              addFilter={addFilter}
              fieldId="feature"
              value={filterData.feature ? filterData.feature : ""}
              headerName="Genomic Symbols"
            />
          </>
        );
      case "attribute":
        return (
          <AttributeFilterInput
            gridRef={gridRef}
            showBuilder={showBuilder}
            addFilter={addFilter}
            filterString={attributeFilterString}
            setFilterString={setAttributeFilterString}
          />
        );
      default:
        return null;
    }
  };

  // Handle sortable order change
  const handleSortChange = (newOrder) => {
    setSortableSections(newOrder);
    // Save to localStorage
    try {
      localStorage.setItem('sidebarFilterSectionOrder', JSON.stringify(newOrder));
    } catch (error) {
      console.warn('Failed to save section order:', error);
    }
  };

  // Initialize tooltips using shared hook
  useTooltips();

  return (
    <ReactSortable
      list={sortableSections}
      setList={handleSortChange}
      animation={150}
      delayOnTouchStart={true}
      delay={2}
      className="d-flex flex-column"
      ghostClass="sortable-ghost"
      chosenClass="sortable-chosen"
      dragClass="sortable-drag"
      handle=".card-header"
    >
      {sortableSections.map((section) => {
        const filterVariant = getSidebarSectionVariant(section.type, filter_results);
        
        // All sections now use the CollapsibleSection pattern
        
        return (
          <div key={section.id} className="sortable-item">
            <CollapsibleSection
              title={section.title}
              isCollapsed={filters[section.type]?.collapsed}
              onToggle={() => toggleCollapse(section.type)}
              variant={filterVariant ? "active" : "default"}
              className={filterVariant ? `filter-variant-${filterVariant}` : ""}
            >
              {renderSectionContent(section.type)}
            </CollapsibleSection>
          </div>
        );
      })}
    </ReactSortable>
  );
};

export default FiltersForm;
